/**
 * Shopping Preferences Modal Hook
 *
 * Manages modal state for shopping preferences.
 * Separated from main preferences hook for better separation of concerns.
 */

import { useState, useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import { usePreferences } from '@context/preferences.context';
import { shouldShowPreferencesModal } from '@lib/shopping-preferences/preference-manager';
import logger from '@helpers/utils/logger/client';
import { useShoppingPreferences } from './use-shopping-preferences';

interface UseShoppingPreferencesModalReturn {
  showModal: boolean;
  setShowModal: (show: boolean) => void;
  hasSeenCMP: boolean;
}

/**
 * Hook to manage shopping preferences modal visibility
 *
 * Modal is shown when:
 * - Feature is enabled
 * - User is on homepage
 * - User hasn't selected preferences yet
 * - Not using session preferences
 * - User has seen CMP (consent management platform)
 * - Not a bot
 */
export const useShoppingPreferencesModal = (): UseShoppingPreferencesModalReturn => {
  const { preferences, source, isEnabled } = useShoppingPreferences();
  const { language } = usePreferences();
  const router = useRouter();
  const pathname = router?.pathname || '/';

  const [showModal, setShowModal] = useState(false);
  const [hasWindowLoaded, setHasWindowLoaded] = useState(false);

  // The CMP popin is showing on every language but US
  // Initialize based on language, but update when language becomes available (important for navigation)
  const [hasSeenCMP, setHasSeenCMP] = useState<boolean>(() => {
    return language ? ['us'].includes(language) : false;
  });

  // Update hasSeenCMP when language becomes available (handles navigation case)
  useEffect(() => {
    if (language && ['us'].includes(language) && !hasSeenCMP) {
      setHasSeenCMP(true);
    }
  }, [language, hasSeenCMP]);

  // Track window load state
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (document.readyState === 'complete') {
      setHasWindowLoaded(true);
    } else {
      const onLoad = (): void => setHasWindowLoaded(true);
      window.addEventListener('load', onLoad);
      return () => window.removeEventListener('load', onLoad);
    }
  }, []);

  // Setup CACT listeners for CMP
  // Use ref to store CACT function to avoid recreating it, but check window.cact on each render
  // to detect when it loads asynchronously
  const cactRef = useRef<typeof window.cact | undefined>(undefined);

  // Track when CACT becomes available to trigger effect re-run
  const cactAvailable = hasWindowLoaded ? !!window?.cact : false;

  useEffect(() => {
    if (!hasWindowLoaded) {
      return;
    }

    // Always check window.cact directly to get latest value
    const cact = window?.cact;

    if (!cact) {
      logger.warn('[Shopping Prefs Modal] Tag Manager (cact) not found');
      return;
    }

    // Update ref with latest CACT function
    cactRef.current = cact;

    if (hasSeenCMP) {
      return;
    }

    logger.info('[Shopping Prefs Modal] Setting up CMP listeners');

    const handleConsentUpdate = () => {
      logger.info('[Shopping Prefs Modal] Consent has been updated');
      setHasSeenCMP(true);
    };

    const handleConsentGet = (result) => {
      logger.info('[Shopping Prefs Modal] Consent data received');
      if (result?.consent?.status !== 'unset') {
        logger.info('[Shopping Prefs Modal] User has seen CMP');
        setHasSeenCMP(true);
      }
    };

    cact('consent.onUpdate', handleConsentUpdate);
    cact('consent.get', handleConsentGet);

    // TODO: Add cleanup if CACT supports removing listeners
  }, [hasWindowLoaded, cactAvailable, hasSeenCMP, pathname]);

  // Calculate if modal should be shown
  const shouldShow = useMemo(() => {
    if (!hasWindowLoaded || !hasSeenCMP) {
      return false;
    }

    return shouldShowPreferencesModal(preferences, source, isEnabled, pathname);
  }, [hasWindowLoaded, hasSeenCMP, preferences, source, isEnabled, pathname]);

  // Update modal visibility based on conditions
  useEffect(() => {
    setShowModal(shouldShow);
  }, [shouldShow]);

  return {
    showModal,
    setShowModal,
    hasSeenCMP,
  };
};

export default useShoppingPreferencesModal;
